import { createContext, useContext, useEffect, useMemo } from 'react'
import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { AuthProvider as OidcProvider, useAuth as useOidc } from 'react-oidc-context'
import { UserManager, WebStorageStateStore } from 'oidc-client-ts'
import { offerPasskeyEnrolment } from './passkeyEnrolment'

const PORTAL_AUDIENCE = 'https://portal.sentosashipbrokers.com'

const AuthContext = createContext(null)

export const useAuth = () => useContext(AuthContext)

const currentPath = () => window.location.pathname + window.location.search

const Auth0Bridge = ({ children }) => {
  const { isLoading, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0()

  const value = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      user: user && { name: user.name || user.nickname, email: user.email, sub: user.sub },
      getAccessTokenSilently: () => getAccessTokenSilently(),
      login: returnTo => loginWithRedirect({ appState: { returnTo } }),
      logout: () => logout({ logoutParams: { returnTo: window.location.origin + '/logout' } }),
    }),
    [isLoading, isAuthenticated, user, getAccessTokenSilently, loginWithRedirect, logout]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const Auth0AuthProvider = ({ children }) => (
  <Auth0Provider
    domain={window.AUTH0_DOMAIN}
    clientId={window.AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: window.location.origin,
      audience: PORTAL_AUDIENCE,
    }}
    useRefreshTokens={true}
    cacheLocation="memory"
  >
    <Auth0Bridge>{children}</Auth0Bridge>
  </Auth0Provider>
)

let keycloakUserManager

const getKeycloakUserManager = () => {
  if (!keycloakUserManager) {
    keycloakUserManager = new UserManager({
      authority: window.KEYCLOAK_AUTHORITY,
      client_id: window.KEYCLOAK_CLIENT_ID,
      redirect_uri: window.location.origin + '/',
      post_logout_redirect_uri: window.location.origin + '/logout',
      scope: 'openid profile email',
      automaticSilentRenew: true,
      userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    })
  }
  return keycloakUserManager
}

const completeKeycloakSignIn = async user => {
  const userManager = getKeycloakUserManager()
  const returnTo = user?.state?.returnTo || '/'

  if (await offerPasskeyEnrolment(userManager, user)) {
    return
  }

  if (returnTo === currentPath()) {
    window.history.replaceState({}, document.title, returnTo)
  } else {
    window.location.replace(returnTo)
  }
}

const KeycloakBridge = ({ children }) => {
  const oidc = useOidc()
  const userManager = getKeycloakUserManager()
  const profile = oidc.user?.profile

  const value = useMemo(
    () => ({
      isLoading: oidc.isLoading || Boolean(oidc.activeNavigator),
      isAuthenticated: oidc.isAuthenticated,
      user: profile && {
        name: profile.name || profile.preferred_username,
        email: profile.email,
        sub: profile.auth0_user_id || profile.sub,
      },
      getAccessTokenSilently: async () => {
        const current = await userManager.getUser()
        const fresh = current && !current.expired ? current : await userManager.signinSilent()
        return fresh.access_token
      },
      login: returnTo => userManager.signinRedirect({ state: { returnTo } }),
      logout: () => userManager.signoutRedirect(),
    }),
    [oidc.isLoading, oidc.activeNavigator, oidc.isAuthenticated, profile, userManager]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const KeycloakAuthProvider = ({ children }) => (
  <OidcProvider userManager={getKeycloakUserManager()} onSigninCallback={completeKeycloakSignIn}>
    <KeycloakBridge>{children}</KeycloakBridge>
  </OidcProvider>
)

export const AuthProvider = ({ children }) =>
  window.AUTH_PROVIDER === 'keycloak' ? (
    <KeycloakAuthProvider>{children}</KeycloakAuthProvider>
  ) : (
    <Auth0AuthProvider>{children}</Auth0AuthProvider>
  )

export const RequireAuth = ({ children }) => {
  const { isLoading, isAuthenticated, login } = useAuth()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      login(currentPath())
    }
  }, [isLoading, isAuthenticated, login])

  return isAuthenticated ? children : null
}
